import type { Amendment, Institution } from '@/services/firestore';

export type ChartDataset = { labels: string[]; data: number[] };

function toMonthLabel(isoDate: string): string {
  const y = isoDate.slice(0, 4);
  const m = isoDate.slice(5, 7);
  return `${y}-${m}`;
}

export function buildCouncilorMonthlyDataset(
  amendments: Amendment[],
  year?: string,
  month?: string
): ChartDataset {
  const filtered = amendments.filter((a) => {
    const d = a.date || '';
    const y = d.slice(0, 4);
    const m = d.slice(5, 7);
    if (year && y !== year) return false;
    if (month && m !== month) return false;
    return true;
  });
  const byMonth = new Map<string, number>();
  for (const a of filtered) {
    const label = toMonthLabel(a.date || '');
    const prev = byMonth.get(label) || 0;
    byMonth.set(label, prev + (a.value || 0));
  }
  const labels = Array.from(byMonth.keys()).sort();
  const data = labels.map((l) => byMonth.get(l) || 0);
  return { labels, data };
}

export function buildInstitutionTotalsDataset(
  amendments: Amendment[],
  institutions: Institution[],
  year?: string,
  month?: string,
  search?: string
): ChartDataset {
  const filtered = amendments.filter((a) => {
    const d = a.date || '';
    const y = d.slice(0, 4);
    const m = d.slice(5, 7);
    if (year && y !== year) return false;
    if (month && m !== month) return false;
    return true;
  });

  const instMap = new Map(institutions.map(i => [i.id, i]));

  // Agrupa por CNPJ (ou ID)
  const groups = new Map<string, { name: string; category: string; total: number }>();

  for (const a of filtered) {
    const inst = instMap.get(a.institutionId);
    if (!inst) continue;

    // Chave de agrupamento: CNPJ (números) ou ID
    const cleanCnpj = inst.cnpj ? inst.cnpj.replace(/\D/g, '') : '';
    const key = cleanCnpj || inst.id;

    if (!groups.has(key)) {
      groups.set(key, {
        name: inst.name,
        category: inst.category || '',
        total: 0
      });
    }
    const g = groups.get(key)!;
    g.total += (a.value || 0);
    // Opcional: concatenar categorias se diferentes? Por simplicidade mantemos a primeira encontrada, assim como na tabela principal
  }

  const rows = Array.from(groups.values());
  const q = (search || '').toLowerCase();

  const searched = q
    ? rows.filter((r) => r.name.toLowerCase().includes(q) || r.category.toLowerCase().includes(q))
    : rows;

  const labels = searched.map((r) => r.name);
  const data = searched.map((r) => r.total);
  return { labels, data };
}

export function buildCouncilorTotalsForInstitution(
  amendments: Amendment[],
  councilors: { id: string; name: string }[],
  institutionId: string,
  year?: string,
  month?: string
): ChartDataset {
  const filtered = amendments.filter((a) => {
    const d = a.date || '';
    const y = d.slice(0, 4);
    const m = d.slice(5, 7);
    if (a.institutionId !== institutionId) return false;
    if (year && y !== year) return false;
    if (month && m !== month) return false;
    return true;
  });
  const totals = new Map<string, number>();
  for (const a of filtered) {
    const prev = totals.get(a.councilorId) || 0;
    totals.set(a.councilorId, prev + (a.value || 0));
  }
  const labels: string[] = [];
  const data: number[] = [];
  for (const [cId, total] of totals.entries()) {
    const name = councilors.find((c) => c.id === cId)?.name || 'Vereador';
    labels.push(name);
    data.push(total);
  }
  return { labels, data };
}
